import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Alert } from 'antd';
import { Button, FormField, Input, Tabs, TabsList, TabsTrigger, TabsContent } from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { authApi } from '../api/auth-api';
import {
  loginPasswordSchema,
  type LoginPasswordFormValues,
  otpRequestSchema,
  type OtpRequestFormValues,
  verifySchema,
  type VerifyFormValues,
  detectChannel,
} from '../model/schemas';
import { applyTokens } from '../model/auth-actions';
import { applyApiFieldErrors } from '@shared/lib/forms/apply-field-errors';
import { mapAuthError } from '../lib/map-auth-error';

export function LoginForm() {
  const { t } = useTranslation();
  return (
    <Tabs defaultValue="password" className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="password">{t('auth.login.tabPassword')}</TabsTrigger>
        <TabsTrigger value="otp">{t('auth.login.tabOtp')}</TabsTrigger>
      </TabsList>
      <TabsContent value="password">
        <PasswordTab />
      </TabsContent>
      <TabsContent value="otp">
        <OtpTab />
      </TabsContent>
    </Tabs>
  );
}

function PasswordTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<LoginPasswordFormValues>({
    resolver: zodResolver(loginPasswordSchema),
    defaultValues: { identity: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: LoginPasswordFormValues) => authApi.loginPassword(values),
    onSuccess: (tokens) => {
      applyTokens(tokens);
      navigate(ROUTES.root, { replace: true });
    },
    onError: (error) => {
      if (!applyApiFieldErrors(error, form.setError)) {
        setSubmitError(mapAuthError(error));
      }
    },
  });

  return (
    <FormProvider {...form}>
    <form
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((v) => {
        setSubmitError(null);
        mutation.mutate(v);
      })}
      noValidate
    >
      <FormField label={t('auth.login.identityLabel')} error={form.formState.errors.identity?.message}>
        {(p) => (
          <Input
            {...p}
            autoComplete="username"
            placeholder={t('auth.login.identityPlaceholder')}
            {...form.register('identity')}
          />
        )}
      </FormField>
      <FormField label={t('common.password')} error={form.formState.errors.password?.message}>
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="current-password"
            {...form.register('password')}
          />
        )}
      </FormField>

      {submitError ? <Alert type="error" showIcon message={submitError} /> : null}

      <Button type="submit" loading={mutation.isPending}>
        {t('auth.login.submit')}
      </Button>
    </form>
    </FormProvider>
  );
}

function OtpTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sentIdentity, setSentIdentity] = useState<string | null>(null);

  const identityForm = useForm<OtpRequestFormValues>({
    resolver: zodResolver(otpRequestSchema),
    defaultValues: { identity: '' },
  });

  const codeForm = useForm<VerifyFormValues>({
    resolver: zodResolver(verifySchema),
    defaultValues: { code: '' },
  });

  const requestMutation = useMutation({
    mutationFn: (values: OtpRequestFormValues) =>
      authApi.requestOtp({ identity: values.identity, channel: detectChannel(values.identity) }),
    onSuccess: (_data, values) => setSentIdentity(values.identity),
    onError: (error) => setSubmitError(mapAuthError(error)),
  });

  const loginMutation = useMutation({
    mutationFn: (values: VerifyFormValues) =>
      authApi.loginOtp({
        identity: sentIdentity!,
        channel: detectChannel(sentIdentity!),
        code: values.code,
      }),
    onSuccess: (tokens) => {
      applyTokens(tokens);
      navigate(ROUTES.root, { replace: true });
    },
    onError: (error) => setSubmitError(mapAuthError(error)),
  });

  if (!sentIdentity) {
    return (
      <FormProvider {...identityForm}>
      <form
        className="flex flex-col gap-4"
        onSubmit={identityForm.handleSubmit((v) => {
          setSubmitError(null);
          requestMutation.mutate(v);
        })}
        noValidate
      >
        <FormField
          label={t('auth.login.identityLabel')}
          error={identityForm.formState.errors.identity?.message}
        >
          {(p) => (
            <Input
              {...p}
              autoComplete="username"
              placeholder={t('auth.login.identityPlaceholder')}
              {...identityForm.register('identity')}
            />
          )}
        </FormField>

        {submitError ? (
          <Alert type="error" showIcon message={submitError} />
        ) : null}

        <Button type="submit" loading={requestMutation.isPending}>
          {t('auth.login.requestCode')}
        </Button>
      </form>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...codeForm}>
    <form
      className="flex flex-col gap-4"
      onSubmit={codeForm.handleSubmit((v) => {
        setSubmitError(null);
        loginMutation.mutate(v);
      })}
      noValidate
    >
      <FormField label={t('common.code')} error={codeForm.formState.errors.code?.message}>
        {(p) => (
          <Input
            {...p}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            className="text-center text-lg tracking-[0.4em]"
            {...codeForm.register('code')}
          />
        )}
      </FormField>

      {submitError ? <Alert type="error" showIcon message={submitError} /> : null}

      <Button type="submit" loading={loginMutation.isPending}>
        {t('auth.login.submit')}
      </Button>
      <Button type="button" variant="ghost" onClick={() => setSentIdentity(null)}>
        {t('common.back')}
      </Button>
    </form>
    </FormProvider>
  );
}
